<script setup lang="ts">
import { ref, watch } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import type { VisionProject } from '@/models/RobVision';
import { addProjectMember } from '@/api/RobVisionApi';
import { emailValidationRule, requiredRule } from '@/helpers/FormRules';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

const props = defineProps<{ isOpen: boolean; project: VisionProject }>();
const emit = defineEmits(['update:isOpen', 'member-added']);

const email = ref('');
const error = ref('');
const submitting = ref(false);
const form = ref();

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      email.value = '';
      error.value = '';
    }
  },
);

const cancel = () => {
  emit('update:isOpen', false);
};

const submit = async () => {
  const { valid } = await form.value.validate();
  if (!valid || submitting.value) {
    return;
  }
  submitting.value = true;
  error.value = '';
  try {
    await addProjectMember(props.project.uuid, email.value.trim());
    emit('member-added');
    emit('update:isOpen', false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    error.value = err.message;
  } finally {
    submitting.value = false;
  }
};
</script>

<template>
  <v-dialog :model-value="isOpen" max-width="600" @click:outside="cancel">
    <v-card>
      <v-card-title>{{ $gettext('Add Member') }}</v-card-title>
      <v-card-text>
        <p class="mb-4">
          {{
            $gettext(
              'Add a new member to "%{ name }" by entering their email address.',
              { name: project.name },
            )
          }}
        </p>
        <v-form ref="form" @submit.prevent="submit">
          <v-text-field
            v-model.trim="email"
            :label="$gettext('Email address')"
            placeholder="name@company.com"
            :rules="[requiredRule, emailValidationRule]"
            autofocus
            @keydown.enter.prevent="submit"
          />
        </v-form>
        <v-alert v-if="error" type="error" density="compact" class="mt-2">
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <r-btn variant="outlined" @click="cancel">
          {{ $gettext('Cancel') }}
        </r-btn>
        <v-spacer />
        <r-btn
          variant="accent"
          icon="mdi-account-plus"
          :loading="submitting"
          @click="submit"
        >
          {{ $gettext('Add Member') }}
        </r-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
